import { useStyles } from '@hooks/useStyles'
import { useWizardContext } from '@pages/Landings/routes/Landings/CreateLandingWizard/contexts'
import { LANDINGS } from '@routes/path'
import { Button, Divider, Icon, Paragraph, Spacer, Spreader } from '@ui-kit'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Column, Row } from 'simple-flexbox'

import styles from './Loader.module.scss'
import { SpinningCircle } from './SpinningCircle'

const getStatusIcon = (status: string) => {
  if (status === 'loading') {
    return <SpinningCircle />
  }

  if (status === 'success') {
    return <Icon icon='icon-check-circle' color='primary' size={14} />
  }

  return <div />
}

const Step = ({
  children,
  status,
  showDividerOnSuccess = true,
  wholeLoadingFinished,
  isLastStep
}: {
  children: string
  status: string
  showDividerOnSuccess?: boolean
  wholeLoadingFinished?: boolean
  isLastStep?: boolean
}) => {
  const isLoading = status === 'loading'

  const stepDividerStyles = useStyles({
    [styles['step-divider']]: true,
    [styles['step-divider--growing']]:
      status === 'success' && !wholeLoadingFinished
  })

  const paragraphStyles = useStyles({
    [styles.text]: true,
    [styles['shiny-text']]: isLoading,
    [styles['text--inactive']]: status === 'inactive'
  })

  const transitionComponent =
    status === 'success' && showDividerOnSuccess ? (
      <Fragment>
        <Spacer space={5} />

        <Divider variant='vertical' height={30} className={stepDividerStyles} />
      </Fragment>
    ) : (
      <Fragment>
        <Spacer space={5} />
        <Spacer space={30} />
      </Fragment>
    )

  return (
    <Column>
      <Spacer space={5} />
      <Row>
        {status === 'inactive' ? (
          <div className={styles['icon-placeholder']} />
        ) : (
          <Row justifyContent='center' alignItems='center'>
            {getStatusIcon(status)}
          </Row>
        )}
        <Spreader spread={10} />
        <Paragraph
          className={paragraphStyles}
          color='neutral'
          weight={400}
          lineHeight='normal'
        >
          {children}
        </Paragraph>
      </Row>
      {!isLastStep && transitionComponent}
    </Column>
  )
}

const initialStepsContent = [
  {
    status: 'loading',
    text: 'translation.create.blank.landing.page'
  },
  {
    status: 'inactive',
    text: 'translation.prepare.layout'
  },
  {
    status: 'inactive',
    text: 'translation.generate.content.for.landing.page'
  },
  {
    status: 'inactive',
    text: 'translation.fill.shapes.with.colours'
  },
  {
    status: 'inactive',
    text: 'translation.generate.images.for.website'
  },
  {
    status: 'inactive',
    text: 'translation.make.website.mobile.friendly'
  },
  {
    status: 'inactive',
    text: 'translation.finalizing',
    showDividerOnSuccess: false
  }
]

const initialSteps = [
  {
    status: 'loading',
    text: 'translation.create.blank.landing.page'
  },
  {
    status: 'inactive',
    text: 'translation.prepare.layout'
  },
  {
    status: 'inactive',
    text: 'translation.fill.shapes.with.colours'
  },
  {
    status: 'inactive',
    text: 'translation.generate.images.for.website'
  },
  {
    status: 'inactive',
    text: 'translation.make.website.mobile.friendly'
  },
  {
    status: 'inactive',
    text: 'translation.finalizing',
    showDividerOnSuccess: false
  }
]

export const Loader = ({ isFinished }: { isFinished: boolean }) => {
  const { shouldGenerateContent } = useWizardContext()
  const [steps, setSteps] = useState(() =>
    shouldGenerateContent
      ? structuredClone(initialStepsContent)
      : structuredClone(initialSteps)
  )
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(0)
  const [shouldShowTakingTooLongMessage, setShouldShowTakingTooLongMessage] =
    useState(false)

  const { t } = useTranslation()

  const increaseStepNumber = useCallback(() => {
    if (currentStep < steps.length - 1) {
      const newStepNumber = currentStep + 1

      setCurrentStep(newStepNumber)
      setSteps(prevSteps => {
        const newSteps = [...prevSteps]
        newSteps[newStepNumber - 1].status = 'success'

        return newSteps
      })

      if (currentStep !== steps.length - 1) {
        setTimeout(() => {
          setSteps(prevSteps => {
            const newSteps = [...prevSteps]
            newSteps[newStepNumber].status = 'loading'

            return newSteps
          })
        }, 1000)
      }
    } else if (!shouldShowTakingTooLongMessage) {
      setTimeout(() => setShouldShowTakingTooLongMessage(true), 30000)
    }
  }, [currentStep, steps.length, shouldShowTakingTooLongMessage])

  useEffect(() => {
    const interval = setInterval(
      increaseStepNumber,
      shouldGenerateContent ? 20000 : 4000
    )

    return () => clearInterval(interval)
  }, [increaseStepNumber, shouldGenerateContent])

  return (
    <div className={styles.container}>
      <div className={styles['circle-group']}>
        <div className={`${styles.blob} ${styles['blob-cn']}`} />
        <div className={`${styles.blob} ${styles['blob-f']}`} />
        <div className={`${styles.blob} ${styles['blob-r']}`} />
        <div className={`${styles.blob} ${styles['blob-p']}`} />
        <div className={`${styles.blob} ${styles['blob-y']}`} />
        <div className={`${styles.blob} ${styles['blob-z']}`} />
        <div className={`${styles.blob} ${styles['blob-t']}`} />
        <div className={`${styles.blob} ${styles['blob-jn']}`} />
      </div>
      <div className={styles.mask}>
        <div>
          {steps.map((step, index) => (
            <Step
              key={step.text}
              status={isFinished ? 'success' : step.status}
              wholeLoadingFinished={isFinished}
              showDividerOnSuccess={step.showDividerOnSuccess}
              isLastStep={index === steps.length - 1}
            >
              {t(step.text)}
            </Step>
          ))}

          {shouldShowTakingTooLongMessage && !isFinished && (
            <Fragment>
              <Spacer space={15} />

              <Row>
                <div className={styles['icon-placeholder']} />
                <Spreader spread={10} />
                <Column>
                  <Paragraph size={12}>
                    {t('wizard.loader.taking.too.long.message')}
                  </Paragraph>

                  <Spacer space={10} />

                  <div>
                    <Button
                      onClick={() => navigate(LANDINGS.LANDINGS)}
                      size={12}
                    >
                      {t('wizard.loader.taking.too.long.button')}
                    </Button>
                  </div>
                </Column>
              </Row>
            </Fragment>
          )}
        </div>
      </div>
    </div>
  )
}
